import React, { useState, useMemo, useEffect } from 'react';
import { doc, updateDoc, deleteField, collection, query, where, getDocs } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { calcularEstadoMoroso, useConfiguracionFinanciera } from '../utils/morosoUtils';
import './ResidentProfileModal.css';
import { FaUser, FaHome, FaCar, FaExclamationCircle, FaFileAlt, FaPaw, FaUsers } from 'react-icons/fa';
import ConfirmDialog from './ConfirmDialog';
import {
  TAG_KEYS,
  getTagLabel,
  convertirTagImpresoAAntena,
  convertirTagAntenaAImpreso,
  auditFraccionamientoTags
} from '../utils/rfidTagUtils';

export {
  TAG_KEYS,
  getTagLabel,
  convertirTagImpresoAAntena,
  convertirTagAntenaAImpreso
};

function TabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button
      type="button"
      className={`rp-tab-btn ${active ? 'active' : ''}`}
      onClick={onClick}
    >
      {Icon ? <Icon aria-hidden="true" /> : null}
      <span>{label}</span>
    </button>
  );
}

const buildTagsList = (source) => {
  if (!source) return [];
  const list = [];
  TAG_KEYS.forEach((key) => {
    const raw = source[key];
    const val = typeof raw === 'string' ? raw.trim() : raw;
    if (val) {
      list.push({ key, value: String(val) });
    }
  });
  return list;
};

const getNextAvailableTagKey = (tags = []) => TAG_KEYS.find((key) => !tags.some((t) => t.key === key)) || null;

export default function ResidentProfileModal({ open, onClose, resident }) {
  const [tab, setTab] = useState('personales');
  const { userData } = useAuth();
  const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd || null;
  const financialConfig = useConfiguracionFinanciera(fraccionamientoId);
  const [isEditing, setIsEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [showSuccessConfirm, setShowSuccessConfirm] = useState(false);
  const [altPhoneIndex, setAltPhoneIndex] = useState(2); // Para el selector de teléfonos alternativos (2-6)
  const [cuentaVinculadaInfo, setCuentaVinculadaInfo] = useState(null);
  const [fraccionamientoTagsMap, setFraccionamientoTagsMap] = useState(new Map());

  // Carga reactiva de todos los tags asignados en el fraccionamiento para blindaje en tiempo real
  useEffect(() => {
    if (!open || !fraccionamientoId) {
      setFraccionamientoTagsMap(new Map());
      return;
    }

    let isMounted = true;
    const cargarTagsFraccionamiento = async () => {
      try {
        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const snap = await getDocs(casasRef);
        if (!isMounted) return;

        const casasList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const { tagToCasaMap } = auditFraccionamientoTags(casasList, resident?.id);
        setFraccionamientoTagsMap(tagToCasaMap);
      } catch (err) {
        console.warn('[ResidentProfileModal] Error cargando mapa de tags para blindaje:', err);
      }
    };

    cargarTagsFraccionamiento();
    return () => { isMounted = false; };
  }, [open, fraccionamientoId, resident?.id]);

  const {
    nombreCompleto,
    identificacion,
    telefono,
    telefonoAlternativo,
    correo,
    fechaNacimiento,
    casaLabel,
    tipoResidente,
    perfil,
    fechaIngreso,
    dormitorios,
    // ocupantes,
    // mascotas,
    vehiculo,
    emergencia,
    notas,
    // id,
    statusLabel,
    mascotasLabel,
    ocupantesLabel,
    tagDisplay,
  } = useMemo(() => {
    const r = resident || {};
    const casa = r.numero || r.numeroCasa || r.lote || r.casaLabel;
    const getValueByKeys = (obj, keys) => {
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
      }
      return undefined;
    };
    const mascotasBooleanKeys = [
      'mascotas/si/no', 'mascotas_si_no', 'mascotasSiNo', 'tiene_mascotas', 'tieneMascotas', 'mascotas_bool'
    ];
    const mascotasCountKeys = [
      'mascotas', 'numMascotas', 'numeroMascotas', 'totalMascotas', 'mascotas_count', 'mascotasRegistradas', 'mascotasDetalle'
    ];
    const ocupantesKeys = [
      'num_residentes', 'numero_residentes', 'numResidentes', 'numeroResidentes',
      'ocupantes', 'numOcupantes', 'numeroOcupantes', 'residents', 'residentsCount'
    ];

    const rawMascotasBoolean = getValueByKeys(r, mascotasBooleanKeys);
    const rawMascotas = rawMascotasBoolean !== undefined ? rawMascotasBoolean : getValueByKeys(r, mascotasCountKeys);
    const rawOcupantes = getValueByKeys(r, ocupantesKeys);
    const toNonNegativeInteger = (value) => {
      if (value === undefined || value === null) return null;
      const n = Number(value);
      if (!Number.isFinite(n) || n < 0) return null;
      return Math.floor(n);
    };
    const buildMascotasLabel = (raw) => {
      if (raw === undefined || raw === null) return '—';
      if (typeof raw === 'boolean') return raw ? 'Sí' : 'No';
      if (typeof raw === 'string') {
        const v = raw.trim().toLowerCase();
        if (['si', 'sí', 'yes', 'true', '1'].includes(v)) return 'Sí';
        if (['no', 'false', '0'].includes(v)) return 'No';
        // if not a yes/no, treat as count/description
        const n = toNonNegativeInteger(v);
        if (n !== null) return n === 0 ? 'Sin mascotas' : `${n} mascota${n === 1 ? '' : 's'}`;
        return raw.trim() || '—';
      }
      if (Array.isArray(raw)) return raw.length === 0 ? 'Sin mascotas' : `${raw.length} mascota${raw.length === 1 ? '' : 's'}`;
      const n = toNonNegativeInteger(raw);
      if (n !== null) return n === 0 ? 'Sin mascotas' : `${n} mascota${n === 1 ? '' : 's'}`;
      return '—';
    };
    const buildOcupantesLabel = (raw) => {
      const n = toNonNegativeInteger(raw);
      if (n !== null) return `${n}`;
      if (typeof raw === 'string') return raw.trim() || '—';
      return '—';
    };
    const tagsList = buildTagsList(r);
    const tagDisplay = tagsList.length
      ? tagsList.map((t) => {
          const impreso = convertirTagAntenaAImpreso(t.value);
          return impreso && impreso !== t.value ? `${t.value} (Impreso: ${impreso})` : t.value;
        }).join(' • ')
      : '—';

    return {
      id: r.id || casa || '',
      nombreCompleto: r.nombre_residente || r.nombreCompleto || r.nombre || '',
      identificacion: r.rfc || r.ine || r.identificacion || '',
      telefono: r.telefono || r.telefonoPrincipal || '',
      telefonoAlternativo: r.telefonoAlternativo || '',
      correo: r.correo || r.email || '',
      fechaNacimiento: r.fechaNacimiento || '',
      casaLabel: casa || '',
      tipoResidente: r.tipoResidente || 'Propietario',
      perfil: r.perfil || 'Propietario',
      fechaIngreso: r.fechaIngreso || '',
      dormitorios: r.dormitorios || r.numDormitorios || '',
      ocupantes: r.ocupantes || r.numOcupantes || r.num_residentes || '',
      mascotas: r.mascotas || '',
      vehiculo: r.vehiculo || r.auto || r.vehiculos || {},
      emergencia: r.emergencia || {},
      notas: r.notas || '',
      statusLabel: (() => {
        const adeudo = Number(r.adeudo || r.deuda || 0);
        const cuota = Number(financialConfig.cuotaMensual || 0);
        const meses = Number(financialConfig.mesesPermitidos || 1);
        return calcularEstadoMoroso(adeudo, cuota, meses) ? 'Moroso' : 'Al corriente';
      })(),
      mascotasLabel: buildMascotasLabel(rawMascotas),
      ocupantesLabel: buildOcupantesLabel(rawOcupantes),
      tagDisplay,
    };
  }, [resident]);

  const [form, setForm] = useState({ tags: [] });
  const [selectedTagKey, setSelectedTagKey] = useState(null);

  useEffect(() => {
    const r = resident || {};
    const tagsFromResident = buildTagsList(r);

    // Detectar vehículos existentes y convertir a array
    let vehiculosArray = [];
    let numVehiculosDetected = 1;

    if (Array.isArray(r.vehiculos) && r.vehiculos.length > 0) {
      // Ya existe como array
      vehiculosArray = r.vehiculos.map(v => ({
        placa: v?.placa || '',
        marca: v?.marca || '',
        modelo: v?.modelo || '',
        color: v?.color || '',
        anio: v?.anio || '',
      }));
      numVehiculosDetected = Math.min(vehiculosArray.length, 3);
    } else if (r.vehiculo && typeof r.vehiculo === 'object' && !Array.isArray(r.vehiculo)) {
      // Existe como objeto único (legacy)
      vehiculosArray = [{
        placa: r.vehiculo?.placa || '',
        marca: r.vehiculo?.marca || '',
        modelo: r.vehiculo?.modelo || '',
        color: r.vehiculo?.color || '',
        anio: r.vehiculo?.anio || '',
      }];
      numVehiculosDetected = 1;
    } else if (Array.isArray(r.vehiculo) && r.vehiculo.length > 0) {
      // Existe como array en campo vehiculo (legacy)
      vehiculosArray = r.vehiculo.map(v => ({
        placa: v?.placa || '',
        marca: v?.marca || '',
        modelo: v?.modelo || '',
        color: v?.color || '',
        anio: v?.anio || '',
      }));
      numVehiculosDetected = Math.min(vehiculosArray.length, 3);
    } else {
      // No hay vehículos, inicializar con 1 vacío
      vehiculosArray = [{ placa: '', marca: '', modelo: '', color: '', anio: '' }];
      numVehiculosDetected = 1;
    }

    // Usar num_vehiculos de Firebase si existe
    if (r.num_vehiculos && typeof r.num_vehiculos === 'number') {
      numVehiculosDetected = Math.min(Math.max(r.num_vehiculos, 1), 3);
    }

    // Asegurar que siempre haya al menos tantos slots como numVehiculos
    while (vehiculosArray.length < numVehiculosDetected) {
      vehiculosArray.push({ placa: '', marca: '', modelo: '', color: '', anio: '' });
    }

    setForm({
      nombreCompleto: r.nombre_residente || r.nombreCompleto || r.nombre || '',
      identificacion: r.rfc || r.ine || r.identificacion || '',
      telefono: r.telefono || r.telefonoPrincipal || '',
      telefono2: r.telefono2 || r.telefonoAlternativo || '', // Mantenemos compatibilidad con telefonoAlternativo
      telefono3: r.telefono3 || '',
      telefono4: r.telefono4 || '',
      telefono5: r.telefono5 || '',
      telefono6: r.telefono6 || '',
      correo: r.correo || r.email || '',
      fechaNacimiento: r.fechaNacimiento || '',
      origen: r.estado || r.origen || '', // Campo Origen → estado en Firebase
      tipoResidente: r.tipoResidente || 'Propietario',
      perfil: r.perfil || 'Propietario',
      propietarioNombre: r.propietarioNombre || r.nombrePropietario || '',
      emailPropietario: r.emailPropietario || r.correoPropietario || '',
      telefonoPropietario: r.telefonoPropietario || '',
      fechaIngreso: r.fechaIngreso || '',
      dormitorios: r.dormitorios || r.numDormitorios || '',
      ocupantes: r.ocupantes || r.numOcupantes || r.num_residentes || '',
      mascotas: r.mascotas || '',
      numVehiculos: numVehiculosDetected,
      vehiculos: vehiculosArray,
      emergencia: {
        nombre: r.emergencia?.nombre || '',
        telefono: r.emergencia?.telefono || '',
        relacion: r.emergencia?.relacion || '',
      },
      notas: r.notas || '',
      tags: tagsFromResident,
    });
    setDirty(false);
    setIsEditing(false);
    setSelectedTagKey(tagsFromResident[0]?.key || null);
  }, [resident]);

  // Reconocimiento inteligente de cuenta existente en Firebase
  useEffect(() => {
    const emailToCheck = (form.correo || resident?.correo || resident?.email || '').trim().toLowerCase();
    if (!emailToCheck) {
      setCuentaVinculadaInfo(null);
      return;
    }

    let isMounted = true;
    const checkEmail = async () => {
      try {
        const usuariosRef = collection(db, 'usuarios');
        const qEmail = query(usuariosRef, where('email', '==', emailToCheck));
        const snapEmail = await getDocs(qEmail);

        if (!isMounted) return;

        if (!snapEmail.empty) {
          const userDataDoc = snapEmail.docs[0].data();
          
          // Consultar directamente cuántos fraccionamientos distintos tienen casas de este usuario
          let esMulti = false;
          try {
            const { collectionGroup } = await import('firebase/firestore');
            const qCasas = query(collectionGroup(db, 'casas'), where('email', '==', emailToCheck));
            const snapCasas = await getDocs(qCasas);
            const fraccsSet = new Set();
            snapCasas.docs.forEach((c) => {
              const f = c.data().fraccionamientoId || c.ref.parent?.parent?.id;
              if (f) fraccsSet.add(f);
            });
            esMulti = fraccsSet.size > 1;
          } catch (eCasas) {
            const fAssigned = userDataDoc.fraccionamientosAsignados || [];
            esMulti = Array.isArray(fAssigned) && fAssigned.length > 1;
          }

          setCuentaVinculadaInfo({
            existe: true,
            multiresidencia: esMulti,
            nombre: userDataDoc.nombre || userDataDoc.displayName || userDataDoc.nombreUsuario || '',
            email: emailToCheck
          });
        } else {
          setCuentaVinculadaInfo(null);
        }
      } catch (err) {
        console.warn('[ResidentProfileModal] Advertencia al consultar cuenta de usuario en Firebase:', err);
      }
    };

    checkEmail();
    return () => { isMounted = false; };
  }, [form.correo, resident, fraccionamientoId]);

  const handleAttemptClose = () => {
    if (dirty) {
      setShowUnsavedConfirm(true);
      return;
    }
    onClose();
  };

  const setField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        obj[p] = obj[p] || {};
        obj = obj[p];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
    setDirty(true);
  };

  const tagsInForm = form.tags || [];
  const selectedTag = useMemo(() => tagsInForm.find((tagEntry) => tagEntry.key === selectedTagKey) || null, [tagsInForm, selectedTagKey]);
  const nextAvailableTagKey = useMemo(() => getNextAvailableTagKey(tagsInForm), [tagsInForm]);

  // Auditoría en tiempo real de duplicados (internos en la misma casa y externos en otras casas)
  const tagConflicts = useMemo(() => {
    const conflicts = {};
    const seenInThisForm = new Map();

    tagsInForm.forEach((tagEntry) => {
      const val = String(tagEntry.value || '').trim();
      if (!val) return;
      const lowerVal = val.toLowerCase();
      const antenaEq = convertirTagImpresoAAntena(val)?.toLowerCase();
      const impresoEq = convertirTagAntenaAImpreso(val)?.toLowerCase();

      // 1. Conflicto interno: duplicado dentro del mismo residente
      const duplicateKey = seenInThisForm.get(lowerVal) || 
        (antenaEq ? seenInThisForm.get(antenaEq) : null) || 
        (impresoEq ? seenInThisForm.get(impresoEq) : null);

      if (duplicateKey) {
        conflicts[tagEntry.key] = {
          type: 'internal',
          message: `Este tag ya fue ingresado en ${getTagLabel(duplicateKey)} de esta misma vivienda.`,
        };
        return;
      }

      seenInThisForm.set(lowerVal, tagEntry.key);
      if (antenaEq) seenInThisForm.set(antenaEq, tagEntry.key);
      if (impresoEq) seenInThisForm.set(impresoEq, tagEntry.key);

      // 2. Conflicto externo: duplicado en otra casa del fraccionamiento
      const external = fraccionamientoTagsMap.get(lowerVal) || 
        (antenaEq ? fraccionamientoTagsMap.get(antenaEq) : null) || 
        (impresoEq ? fraccionamientoTagsMap.get(impresoEq) : null);

      if (external) {
        conflicts[tagEntry.key] = {
          type: 'external',
          message: `Este tag ya está registrado en Casa ${external.numeroCasa} (${external.nombreResidente}) en su ${external.tagLabel}.`,
          detail: external,
        };
      }
    });

    return conflicts;
  }, [tagsInForm, fraccionamientoTagsMap]);

  const hasTagConflicts = Object.keys(tagConflicts).length > 0;

  const handleSelectTag = (key) => {
    setSelectedTagKey(key || null);
  };

  const handleAddTag = () => {
    const nextKey = nextAvailableTagKey;
    if (!nextKey) {
      alert('Ya alcanzaste el máximo de 9 tags.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      tags: [...(prev.tags || []), { key: nextKey, value: '' }],
    }));
    setSelectedTagKey(nextKey);
    setDirty(true);
  };

  const handleTagValueChange = (value) => {
    setForm((prev) => ({
      ...prev,
      tags: (prev.tags || []).map((tagEntry) => tagEntry.key === selectedTagKey ? { ...tagEntry, value } : tagEntry),
    }));
    setDirty(true);
  };

  const handleDeleteTag = () => {
    if (!selectedTagKey) return;
    setForm((prev) => {
      const remaining = (prev.tags || []).filter((tagEntry) => tagEntry.key !== selectedTagKey);
      return { ...prev, tags: remaining };
    });
    setSelectedTagKey((prevKey) => {
      if (!prevKey) return null;
      const remaining = tagsInForm.filter((tagEntry) => tagEntry.key !== prevKey);
      return remaining[0]?.key || getNextAvailableTagKey(remaining) || null;
    });
    setDirty(true);
  };

  /**
   * Validador de teléfono: Solo números, máx 10 dígitos.
   */
  const handlePhoneChange = (field, value) => {
    // Eliminar cualquier cosa que no sea número
    const numericValue = value.replace(/\D/g, '').slice(0, 10);
    setField(field, numericValue);
  };

  if (!open) return null;

  const handleSave = async () => {
    try {
      if (!fraccionamientoId || !resident?.id) throw new Error('Faltan identificadores');
      const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', resident.id);

      const payload = {
        // Datos Personales
        nombre_residente: form.nombreCompleto,
        nombreCompleto: form.nombreCompleto,
        identificacion: form.identificacion,
        telefono: form.telefono,
        telefono2: form.telefono2,
        telefono3: form.telefono3,
        telefono4: form.telefono4,
        telefono5: form.telefono5,
        telefono6: form.telefono6,
        telefonoAlternativo: form.telefono2, // Compatibilidad legacy
        correo: form.correo,
        email: form.correo,
        fechaNacimiento: form.fechaNacimiento,

        // Residencia
        tipoResidente: form.tipoResidente,
        perfil: form.perfil,
        propietarioNombre: form.propietarioNombre || '',
        emailPropietario: (form.emailPropietario || '').trim().toLowerCase(),
        correoPropietario: (form.emailPropietario || '').trim().toLowerCase(),
        telefonoPropietario: form.telefonoPropietario || '',
        fechaIngreso: form.fechaIngreso,
        dormitorios: form.dormitorios,
        num_residentes: form.ocupantes,
        ocupantes: form.ocupantes,
        mascotas: form.mascotas,

        // Vehículos
        num_vehiculos: form.numVehiculos || 1,
        vehiculos: form.vehiculos ? form.vehiculos.slice(0, form.numVehiculos).filter(v =>
          v.placa || v.marca || v.modelo || v.color || v.anio
        ) : [],
        vehiculo: form.vehiculos && form.vehiculos[0] ? form.vehiculos[0] : null,

        // Origen
        estado: form.origen || '',
        origen: form.origen || '',

        // Emergencia
        emergencia: form.emergencia,

        // Notas
        notas: form.notas,

        updatedAt: new Date().toISOString(),
      };

      const cleanTags = {};
      const keysToDelete = new Set();
      const tagsPresentKeys = new Set(tagsInForm.map((tagEntry) => tagEntry.key));
      const invalidTags = [];

      tagsInForm.forEach((tagEntry) => {
        const rawValue = String(tagEntry.value || '').trim();
        if (!rawValue) {
          if (resident?.[tagEntry.key]) {
            keysToDelete.add(tagEntry.key);
          }
          return;
        }
        if (!/^[a-zA-Z0-9]+$/.test(rawValue)) {
          invalidTags.push(getTagLabel(tagEntry.key));
          return;
        }
        cleanTags[tagEntry.key] = rawValue;
      });

      TAG_KEYS.forEach((key) => {
        if (!tagsPresentKeys.has(key) && resident?.[key]) {
          keysToDelete.add(key);
        }
      });

      if (invalidTags.length > 0) {
        alert(`Los siguientes tags solo permiten letras y números: ${invalidTags.join(', ')}`);
        return;
      }

      // Bloqueo estricto si hay conflictos detectados en tiempo real
      if (hasTagConflicts) {
        const firstKey = Object.keys(tagConflicts)[0];
        alert(`⛔ Conflicto de blindaje: ${tagConflicts[firstKey].message}`);
        return;
      }

      // Validar duplicados de tags en Firestore (Auditoría integral en los 9 slots + candidatos antena/impreso)
      const tagsToCheckKeys = Object.keys(cleanTags);
      if (tagsToCheckKeys.length > 0) {
        const queryPromises = [];
        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        
        for (const tagKey of tagsToCheckKeys) {
          const tagValue = cleanTags[tagKey];
          const candidateValues = new Set([tagValue]);
          const ant = convertirTagImpresoAAntena(tagValue);
          if (ant) candidateValues.add(ant);
          const imp = convertirTagAntenaAImpreso(tagValue);
          if (imp) candidateValues.add(imp);

          candidateValues.forEach((candidate) => {
            TAG_KEYS.forEach((tk) => {
              const q = query(casasRef, where(tk, '==', candidate));
              queryPromises.push(
                getDocs(q).then((snap) => ({
                  key: tagKey,
                  value: tagValue,
                  candidateVal: candidate,
                  slot: tk,
                  snap,
                }))
              );
            });
          });
        }
        
        try {
          const queryResults = await Promise.all(queryPromises);
          for (const res of queryResults) {
            const otherDocs = res.snap.docs.filter((d) => d.id !== resident.id);
            if (otherDocs.length > 0) {
              const otherCasa = otherDocs[0].data();
              const otherName = otherCasa.nombre_residente || otherCasa.nombre || 'Otro residente';
              const otherNum = otherCasa.numero || otherCasa.numeroCasa || 'S/N';
              alert(`⛔ Error de blindaje: El tag "${res.value}" ya está registrado en la casa ${otherNum} (${otherName}) en ${getTagLabel(res.slot)}. Por favor usa un tag diferente.`);
              return;
            }
          }
        } catch (errQ) {
          console.error('Error al validar duplicidad de tags:', errQ);
        }
      }

      TAG_KEYS.forEach((key) => {
        if (cleanTags[key]) {
          payload[key] = cleanTags[key];
        } else if (keysToDelete.has(key)) {
          payload[key] = deleteField();
        }
      });

      // 1. Sincronización Maestra (Auth + Colección Usuarios + Casas para campos de identidad)
      let canonicalUidFound = null;
      try {
        let targetUid = resident.userId || resident.uid || resident.usuarioId || resident.uid_residente;
        const targetEmail = (form.correo || '').trim().toLowerCase();
        const originalEmail = (resident.correo || resident.email || '').trim().toLowerCase();

        // Si la casa no guarda el UID directo, resolverlo desde la colección 'usuarios'
        if (!targetUid || targetUid.toString().startsWith('casa_')) {
          const usuariosRef = collection(db, 'usuarios');
          const numCasa = resident.numero || resident.numeroCasa || resident.casaLabel;
          const userEmail = targetEmail || originalEmail;

          let foundDoc = null;
          if (userEmail) {
            const qEmail = query(usuariosRef, where('email', '==', userEmail));
            const snapEmail = await getDocs(qEmail);
            if (!snapEmail.empty) foundDoc = snapEmail.docs[0];
          }
          if (!foundDoc && fraccionamientoId && numCasa) {
            const qCasa = query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId), where('numeroCasa', '==', String(numCasa)));
            const snapCasa = await getDocs(qCasa);
            if (!snapCasa.empty) foundDoc = snapCasa.docs[0];
          }
          if (foundDoc) {
            targetUid = foundDoc.id;
          }
        }

        // Llamar a updateUserAuthEmail si hay UID o correo para sincronizar/vincular
        if (targetUid || targetEmail) {
          const updateAuthEmailFn = httpsCallable(functions, 'updateUserAuthEmail');
          
          const result = await updateAuthEmailFn({
            uid: targetUid || undefined,
            newName: form.nombreCompleto,
            newEmail: targetEmail || undefined,
            newPhone: form.telefono,
            fraccionamientoId: fraccionamientoId,
            casaId: resident.id,
            emailPropietario: (form.emailPropietario || '').trim().toLowerCase(),
            nombrePropietario: form.propietarioNombre || '',
            telefonoPropietario: form.telefonoPropietario || ''
          });

          if (result?.data?.canonicalUid) {
            canonicalUidFound = result.data.canonicalUid;
            targetUid = result.data.canonicalUid;
            payload.userId = canonicalUidFound;
            payload.uid = canonicalUidFound;
          }

          // Actualización directa cliente en la colección 'usuarios' para asegurar campos displayName y nombreUsuario
          if (targetUid && !targetUid.toString().startsWith('casa_')) {
            try {
              const userDocRef = doc(db, 'usuarios', targetUid);
              const nuevoNombre = form.nombreCompleto?.trim();
              if (nuevoNombre) {
                const userClientUpdate = {
                  displayName: nuevoNombre,
                  nombreUsuario: nuevoNombre,
                  nombre: nuevoNombre,
                  nombreCompleto: nuevoNombre,
                  'customFields.displayName': nuevoNombre,
                };
                if (targetEmail) {
                  userClientUpdate.email = targetEmail;
                  userClientUpdate.correo = targetEmail;
                  userClientUpdate['customFields.email'] = targetEmail;
                }
                if (form.telefono) {
                  userClientUpdate.telefono = form.telefono;
                }
                await updateDoc(userDocRef, userClientUpdate);
              }
            } catch (uErr) {
              console.warn('[ResidentProfileModal] No se pudo actualizar doc usuarios cliente:', uErr);
            }
          }
          console.log('[ResidentProfileModal] Sincronización maestra completada (Auth/Usuarios/Casas).');
        } else {
          console.warn('[ResidentProfileModal] Saltando sincronización de Auth: No hay UID ni correo válido.');
        }
      } catch (authErr) {
        console.warn('[ResidentProfileModal] Advertencia en sincronización de usuario:', authErr);
        // NUNCA bloqueamos el guardado de los datos de la vivienda por una advertencia de Auth
      }

      // 2. Actualizar Colección Casas para el resto de los campos (vehículos, etc.)
      if (canonicalUidFound) {
        payload.userId = canonicalUidFound;
        payload.uid = canonicalUidFound;
      }
      await updateDoc(casaRef, payload);

      setIsEditing(false);
      setDirty(false);
      setShowSuccessConfirm(true);
    } catch (err) {
      console.error('Error al guardar perfil:', err);
      alert('No se pudo guardar los cambios');
    }
  };

  return (
    <div className="rp-modal-backdrop" onClick={handleAttemptClose}>
      <div className={`rp-modal ${isEditing ? 'rp-modal-editing' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="rp-header">
          <div 
            className="rp-avatar"
            style={{
              fontSize: String(casaLabel || '').length > 4 ? '12px' : String(casaLabel || '').length >= 4 ? '13px' : '16px'
            }}
          >
            {String(casaLabel || '—')}
          </div>
          <div className="rp-title-wrap">
            <div className="rp-name">{nombreCompleto || '—'}</div>
            <div className={`rp-status ${statusLabel === 'Al corriente' ? 'ok' : 'due'}`}>{statusLabel}</div>
          </div>
          <div className="rp-actions">
            <button type="button" className={`rp-edit ${isEditing ? 'rp-top-cancel-btn' : ''}`} onClick={() => setIsEditing((v) => !v)}>{isEditing ? 'Cancelar' : 'Editar'}</button>
            {isEditing && (
              <button 
                type="button" 
                className="rp-save rp-top-save-btn" 
                onClick={handleSave}
                disabled={hasTagConflicts}
                style={hasTagConflicts ? { opacity: 0.5, cursor: 'not-allowed', filter: 'grayscale(1)' } : undefined}
                title={hasTagConflicts ? 'Hay tags duplicados que deben corregirse antes de guardar' : undefined}
              >
                Guardar
              </button>
            )}
            <button type="button" className="rp-close" onClick={handleAttemptClose} aria-label="Cerrar">✕</button>
          </div>
        </div>

        <div className="rp-tabs">
          <TabButton icon={FaUser} label="Datos Personales" active={tab === 'personales'} onClick={() => setTab('personales')} />
          <TabButton icon={FaHome} label="Residencia" active={tab === 'residencia'} onClick={() => setTab('residencia')} />
          <TabButton icon={FaCar} label="Vehículos" active={tab === 'vehiculos'} onClick={() => setTab('vehiculos')} />
          <TabButton icon={FaExclamationCircle} label="Emergencia" active={tab === 'emergencia'} onClick={() => setTab('emergencia')} />
          <TabButton icon={FaFileAlt} label="Notas" active={tab === 'notas'} onClick={() => setTab('notas')} />
        </div>

        <div className="rp-body">
          {tab === 'personales' && (
            <div className="rp-card-grid">
              <div className="rp-field"><label>Nombre Completo</label>{isEditing ? (<input className="rp-input" value={form.nombreCompleto || ''} onChange={(e) => setField('nombreCompleto', e.target.value)} />) : (<div className="rp-input">{nombreCompleto || '—'}</div>)}</div>
              <div className="rp-field"><label>Identificación (RFC/INE)</label>{isEditing ? (<input className="rp-input" value={form.identificacion || ''} onChange={(e) => setField('identificacion', e.target.value)} />) : (<div className="rp-input">{identificacion || '—'}</div>)}</div>
              <div className="rp-field">
                <label className="rp-label-row">
                  <span>Teléfono Principal</span>
                </label>
                {isEditing ? (
                  <input 
                    className="rp-input" 
                    value={form.telefono || ''} 
                    onChange={(e) => handlePhoneChange('telefono', e.target.value)} 
                    placeholder="10 dígitos numéricos"
                  />
                ) : (
                  <div className="rp-input">{form.telefono || '—'}</div>
                )}
              </div>
              <div className="rp-field">
                <label className="rp-label-row">
                  <span>Alternativo</span>
                  {isEditing ? (
                    <div className="rp-alt-phone-wrapper" title="Selecciona para ver o agregar otro teléfono alternativo (#2 al #6)">
                      <select 
                        className="rp-select-inline"
                        value={altPhoneIndex} 
                        onChange={(e) => setAltPhoneIndex(parseInt(e.target.value, 10))}
                        aria-label="Seleccionar teléfono alternativo"
                      >
                        <option value={2}>Tel #2 ▾</option>
                        <option value={3}>Tel #3 ▾</option>
                        <option value={4}>Tel #4 ▾</option>
                        <option value={5}>Tel #5 ▾</option>
                        <option value={6}>Tel #6 ▾</option>
                      </select>
                    </div>
                  ) : (
                    <span className="rp-alt-phone-badge">Tel #{altPhoneIndex}</span>
                  )}
                </label>
                {isEditing ? (
                  <input 
                    className="rp-input" 
                    value={form[`telefono${altPhoneIndex}`] || ''} 
                    onChange={(e) => handlePhoneChange(`telefono${altPhoneIndex}`, e.target.value)} 
                    placeholder="Opcional - 10 dígitos"
                  />
                ) : (
                  <div className="rp-input">{form[`telefono${altPhoneIndex}`] || 'Opcional'}</div>
                )}
              </div>
              <div className="rp-field">
                <div className="rp-label-row">
                  <label>Correo Electrónico</label>
                  {cuentaVinculadaInfo?.existe && (
                    <span className="rp-badge-vinculado" title="Cuenta activa registrada en Firebase">
                      {cuentaVinculadaInfo.multiresidencia ? '🛡️ Multiresidencia en Firebase' : '✓ Cuenta en Firebase'}
                    </span>
                  )}
                </div>
                {isEditing ? (
                  <>
                    <input 
                      className="rp-input" 
                      value={form.correo || ''} 
                      onChange={(e) => setField('correo', e.target.value)} 
                      placeholder="ejemplo@correo.com"
                    />
                    {cuentaVinculadaInfo?.existe && (
                      <span className="rp-helper-vinculado">
                        ✓ Correo reconocido en Firebase: Se vinculará automáticamente a la cuenta existente sin conflictos.
                      </span>
                    )}
                  </>
                ) : (
                  <div className="rp-input">
                    {correo || '—'}
                    {cuentaVinculadaInfo?.existe && (
                      <span className="rp-tag-vinculado-inline">
                        {cuentaVinculadaInfo.multiresidencia ? 'Multiresidencia' : 'Usuario Firebase'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="rp-field"><label>Fecha de Nacimiento</label>{isEditing ? (<input className="rp-input" type="date" value={form.fechaNacimiento || ''} onChange={(e) => setField('fechaNacimiento', e.target.value)} />) : (<div className="rp-input">{fechaNacimiento || '—'}</div>)}</div>
              <div className="rp-field"><label>Origen</label>{isEditing ? (<input className="rp-input" value={form.origen || ''} onChange={(e) => setField('origen', e.target.value)} placeholder="Ej: Querétaro, San Juan del Río" />) : (<div className="rp-input">{form.origen || resident?.estado || resident?.origen || '—'}</div>)}</div>
            </div>
          )}

          {tab === 'residencia' && (
            <div className="rp-card-grid">
              <div className="rp-field"><label>Casa/Lote</label><div className="rp-input">{casaLabel || '—'}</div></div>
              <div className="rp-field"><label>Perfil de Vivienda</label>{isEditing ? (<select className="rp-input" value={form.perfil || 'Propietario'} onChange={(e) => setField('perfil', e.target.value)}><option value="Propietario">Propietario</option><option value="Arrendador">Arrendador (Rentada)</option></select>) : (<div className="rp-input">{perfil === 'Arrendador' ? 'Arrendador (Rentada)' : (perfil || '—')}</div>)}</div>
              <div className="rp-field"><label>Fecha de Ingreso</label>{isEditing ? (<input className="rp-input" value={form.fechaIngreso || ''} onChange={(e) => setField('fechaIngreso', e.target.value)} />) : (<div className="rp-input">{fechaIngreso || '—'}</div>)}</div>
              <div className="rp-field"><label>Número de Dormitorios</label>{isEditing ? (<input className="rp-input" value={form.dormitorios || ''} onChange={(e) => setField('dormitorios', e.target.value)} />) : (<div className="rp-input">{dormitorios || '—'}</div>)}</div>
              <div className="rp-field"><label>Número de Ocupantes</label>{isEditing ? (<input className="rp-input" value={form.ocupantes || ''} onChange={(e) => setField('ocupantes', e.target.value)} />) : (<div className="rp-input"><FaUsers aria-hidden="true" style={{ marginRight: 8 }} />{ocupantesLabel}</div>)}</div>
              <div className="rp-field"><label>Mascotas</label>{isEditing ? (<input className="rp-input" value={form.mascotas || ''} onChange={(e) => setField('mascotas', e.target.value)} />) : (<div className="rp-input"><FaPaw aria-hidden="true" style={{ marginRight: 8 }} />{mascotasLabel}</div>)}</div>

              {/* Sección Datos del Propietario / Dueño del Inmueble */}
              <div className="rp-field" style={{ gridColumn: '1 / -1', marginTop: 8, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>
                    🏢 Datos del Propietario (Dueño del Inmueble)
                  </span>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    Acceso independiente para revisión financiera y multiresidencia
                  </span>
                </div>
              </div>
              <div className="rp-field">
                <label>Nombre del Propietario</label>
                {isEditing ? (
                  <input
                    className="rp-input"
                    value={form.propietarioNombre || ''}
                    onChange={(e) => setField('propietarioNombre', e.target.value)}
                    placeholder="Nombre completo del dueño"
                  />
                ) : (
                  <div className="rp-input">{form.propietarioNombre || resident?.propietarioNombre || '—'}</div>
                )}
              </div>
              <div className="rp-field">
                <label>Correo del Propietario (Segundo Correo)</label>
                {isEditing ? (
                  <input
                    className="rp-input"
                    value={form.emailPropietario || ''}
                    onChange={(e) => setField('emailPropietario', e.target.value)}
                    placeholder="correo.propietario@ejemplo.com"
                  />
                ) : (
                  <div className="rp-input">{form.emailPropietario || resident?.emailPropietario || '—'}</div>
                )}
              </div>
              <div className="rp-field">
                <label>Teléfono del Propietario</label>
                {isEditing ? (
                  <input
                    className="rp-input"
                    value={form.telefonoPropietario || ''}
                    onChange={(e) => setField('telefonoPropietario', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="10 dígitos del dueño"
                  />
                ) : (
                  <div className="rp-input">{form.telefonoPropietario || resident?.telefonoPropietario || '—'}</div>
                )}
              </div>
            </div>
          )}


          {tab === 'vehiculos' && (
            <div className="rp-card-grid">
              {isEditing && (
                <div className="rp-field" style={{ gridColumn: '1 / -1' }}>
                  <label>¿Cuántos vehículos?</label>
                  <select
                    className="rp-input"
                    value={form.numVehiculos || 1}
                    onChange={(e) => {
                      const newCount = parseInt(e.target.value, 10);
                      setField('numVehiculos', newCount);
                      // Asegurar que el array tenga suficientes elementos
                      const currentVehiculos = form.vehiculos || [];
                      while (currentVehiculos.length < newCount) {
                        currentVehiculos.push({ placa: '', marca: '', modelo: '', color: '', anio: '' });
                      }
                      setForm(prev => ({ ...prev, vehiculos: currentVehiculos }));
                      setDirty(true);
                    }}
                  >
                    <option value={1}>1 vehículo</option>
                    <option value={2}>2 vehículos</option>
                    <option value={3}>3 vehículos</option>
                  </select>
                </div>
              )}

              {/* Renderizar campos para cada vehículo */}
              {Array.from({ length: isEditing ? (form.numVehiculos || 1) : (form.vehiculos?.length || 1) }).map((_, index) => {
                const vehiculo = form.vehiculos?.[index] || {};
                const showVehiculo = isEditing || (vehiculo.placa || vehiculo.marca || vehiculo.modelo || vehiculo.color || vehiculo.anio);

                if (!showVehiculo && !isEditing) return null;

                return (
                  <React.Fragment key={index}>
                    {(form.numVehiculos > 1 || form.vehiculos?.length > 1) && (
                      <div className="rp-field" style={{ gridColumn: '1 / -1', marginTop: index > 0 ? '1rem' : '0' }}>
                        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          Vehículo {index + 1}
                        </h4>
                      </div>
                    )}
                    <div className="rp-field">
                      <label>Placa</label>
                      {isEditing ? (
                        <input
                          className="rp-input"
                          value={vehiculo.placa || ''}
                          onChange={(e) => {
                            const newVehiculos = [...(form.vehiculos || [])];
                            newVehiculos[index] = { ...newVehiculos[index], placa: e.target.value };
                            setForm(prev => ({ ...prev, vehiculos: newVehiculos }));
                            setDirty(true);
                          }}
                          placeholder="Ej: ABC-123"
                        />
                      ) : (
                        <div className="rp-input">{vehiculo.placa || '—'}</div>
                      )}
                    </div>
                    <div className="rp-field">
                      <label>Marca</label>
                      {isEditing ? (
                        <input
                          className="rp-input"
                          value={vehiculo.marca || ''}
                          onChange={(e) => {
                            const newVehiculos = [...(form.vehiculos || [])];
                            newVehiculos[index] = { ...newVehiculos[index], marca: e.target.value };
                            setForm(prev => ({ ...prev, vehiculos: newVehiculos }));
                            setDirty(true);
                          }}
                          placeholder="Ej: Toyota"
                        />
                      ) : (
                        <div className="rp-input">{vehiculo.marca || '—'}</div>
                      )}
                    </div>
                    <div className="rp-field">
                      <label>Modelo</label>
                      {isEditing ? (
                        <input
                          className="rp-input"
                          value={vehiculo.modelo || ''}
                          onChange={(e) => {
                            const newVehiculos = [...(form.vehiculos || [])];
                            newVehiculos[index] = { ...newVehiculos[index], modelo: e.target.value };
                            setForm(prev => ({ ...prev, vehiculos: newVehiculos }));
                            setDirty(true);
                          }}
                          placeholder="Ej: Corolla"
                        />
                      ) : (
                        <div className="rp-input">{vehiculo.modelo || '—'}</div>
                      )}
                    </div>
                    <div className="rp-field">
                      <label>Color</label>
                      {isEditing ? (
                        <input
                          className="rp-input"
                          value={vehiculo.color || ''}
                          onChange={(e) => {
                            const newVehiculos = [...(form.vehiculos || [])];
                            newVehiculos[index] = { ...newVehiculos[index], color: e.target.value };
                            setForm(prev => ({ ...prev, vehiculos: newVehiculos }));
                            setDirty(true);
                          }}
                          placeholder="Ej: Blanco"
                        />
                      ) : (
                        <div className="rp-input">{vehiculo.color || '—'}</div>
                      )}
                    </div>
                    <div className="rp-field">
                      <label>Año</label>
                      {isEditing ? (
                        <input
                          className="rp-input"
                          value={vehiculo.anio || ''}
                          onChange={(e) => {
                            const newVehiculos = [...(form.vehiculos || [])];
                            newVehiculos[index] = { ...newVehiculos[index], anio: e.target.value };
                            setForm(prev => ({ ...prev, vehiculos: newVehiculos }));
                            setDirty(true);
                          }}
                          placeholder="Ej: 2020"
                          maxLength={4}
                        />
                      ) : (
                        <div className="rp-input">{vehiculo.anio || '—'}</div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}

              {/* Tags de Acceso */}
              <div className="rp-field rp-tag-field" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}><label>Tags de Acceso</label>{isEditing ? (
                <div className="rp-tag-editor">
                  <div className="rp-tag-select-row">
                    <select className="rp-input" value={selectedTagKey || ''} onChange={(e) => handleSelectTag(e.target.value || null)}>
                      {tagsInForm.length === 0 ? <option value="">Sin tags</option> : null}
                      {tagsInForm.map((tagEntry) => {
                        const isConflicted = Boolean(tagConflicts[tagEntry.key]);
                        return (
                          <option key={tagEntry.key} value={tagEntry.key}>
                            {`${isConflicted ? '⛔ ' : ''}${getTagLabel(tagEntry.key)}${tagEntry.value ? ` • ${tagEntry.value}` : ''}${isConflicted ? ' (DUPLICADO)' : ''}`}
                          </option>
                        );
                      })}
                    </select>
                    <button type="button" className="rp-tag-add" onClick={handleAddTag} disabled={!nextAvailableTagKey}>Agregar tag</button>
                  </div>
                  {selectedTag ? (() => {
                    const currentVal = (selectedTag.value || '').trim();
                    const conflictInfo = tagConflicts[selectedTag.key];
                    const antenaCandidate = convertirTagImpresoAAntena(currentVal);
                    const impresoCandidate = convertirTagAntenaAImpreso(currentVal);

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="rp-tag-input-row">
                          <input
                            className="rp-input"
                            value={selectedTag.value || ''}
                            onChange={(e) => handleTagValueChange(e.target.value)}
                            placeholder="Ej: 08421455 o 05526479"
                            style={conflictInfo ? {
                              borderColor: '#ef4444',
                              backgroundColor: '#fef2f2',
                              boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.2)',
                              fontWeight: 'bold',
                              color: '#991b1b',
                            } : undefined}
                          />
                          <button type="button" className="rp-delete-tag" onClick={handleDeleteTag}>Eliminar</button>
                        </div>

                        {/* Alerta de Conflicto de Duplicidad en tiempo real */}
                        {conflictInfo && (
                          <div className="rp-tag-conflict-alert">
                            <FaExclamationCircle className="rp-tag-conflict-icon" />
                            <div>
                              <strong>⛔ Conflicto de Duplicidad:</strong> {conflictInfo.message}
                              <div style={{ fontSize: '0.78rem', marginTop: '2px', opacity: 0.9 }}>
                                Para evitar cruces de identidad y errores en la bitácora de caseta, no se permite registrar tags duplicados.
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Asistente inteligente para Tags UHF de Largo Alcance */}
                        {antenaCandidate && antenaCandidate !== currentVal && (
                          <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#f0fdf4',
                            border: '1px solid #86efac',
                            borderRadius: '6px',
                            fontSize: '0.82rem',
                            color: '#166534',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            flexWrap: 'wrap'
                          }}>
                            <div>
                              <span>💡 <strong>Tag impreso detectado:</strong> <code>{currentVal}</code></span>
                              <span style={{ margin: '0 6px' }}>➔</span>
                              <span>Antena ZKTeco detectará: <strong><code>{antenaCandidate}</code></strong></span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleTagValueChange(antenaCandidate)}
                              style={{
                                backgroundColor: '#16a34a',
                                color: '#ffffff',
                                border: 'none',
                                padding: '5px 12px',
                                borderRadius: '4px',
                                fontSize: '0.78rem',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                              }}
                              title="Reemplaza el número impreso por el código que detecta el panel"
                            >
                              ⚡ Convertir a código de antena ({antenaCandidate})
                            </button>
                          </div>
                        )}

                        {impresoCandidate && impresoCandidate !== currentVal && (
                          <div style={{
                            padding: '6px 12px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            color: '#1e40af',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            flexWrap: 'wrap'
                          }}>
                            <div>
                              <span>📡 <strong>Código Antena:</strong> <code>{currentVal}</code></span>
                              <span style={{ margin: '0 6px' }}>•</span>
                              <span>Tag impreso en plástico: <strong><code>{impresoCandidate}</code></strong></span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleTagValueChange(impresoCandidate)}
                              style={{
                                backgroundColor: '#e0e7ff',
                                color: '#3730a3',
                                border: '1px solid #c7d2fe',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                fontSize: '0.72rem',
                                cursor: 'pointer'
                              }}
                              title="Cambiar al número impreso en el plástico del tag"
                            >
                              Ver número impreso ({impresoCandidate})
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="rp-input">Selecciona o agrega un tag para editarlo</div>
                  )}
                  <div className="rp-tag-hint">Solo letras y números. Puedes capturar el código leído por la antena (ej: 08421455) o el impreso en el tag (ej: 05526479).</div>
                </div>
              ) : (
                <div className="rp-input">{tagDisplay}</div>
              )}</div>
            </div>
          )}

          {tab === 'emergencia' && (
            <div className="rp-card-grid rp-accent">
              <div className="rp-field"><label>Nombre</label>{isEditing ? (<input className="rp-input" value={form.emergencia?.nombre || ''} onChange={(e) => setField('emergencia.nombre', e.target.value)} />) : (<div className="rp-input">{emergencia.nombre || '—'}</div>)}</div>
              <div className="rp-field"><label>Teléfono</label>{isEditing ? (<input className="rp-input" value={form.emergencia?.telefono || ''} onChange={(e) => setField('emergencia.telefono', e.target.value)} />) : (<div className="rp-input">{emergencia.telefono || '—'}</div>)}</div>
              <div className="rp-field"><label>Relación</label>{isEditing ? (<input className="rp-input" value={form.emergencia?.relacion || ''} onChange={(e) => setField('emergencia.relacion', e.target.value)} />) : (<div className="rp-input">{emergencia.relacion || '—'}</div>)}</div>
            </div>
          )}

          {tab === 'notas' && (
            <div className="rp-notes">
              <div className="rp-field"><label>Observaciones Generales</label>{isEditing ? (<textarea className="rp-textarea" value={form.notas || ''} onChange={(e) => setField('notas', e.target.value)} />) : (<textarea className="rp-textarea" value={notas || ''} readOnly />)}</div>
              <div className="rp-field"><label>Alergias o Condiciones Médicas</label><textarea className="rp-textarea" placeholder="Información médica relevante para emergencias..." readOnly /></div>
            </div>
          )}
        </div>

        {isEditing && (
          <div className="rp-bottom-edit-actions">
            <button 
              type="button" 
              className="rp-bottom-btn-cancel" 
              onClick={() => setIsEditing(false)}
            >
              Cancelar
            </button>
            <button 
              type="button" 
              className="rp-bottom-btn-save" 
              onClick={handleSave}
              disabled={hasTagConflicts}
              style={hasTagConflicts ? { opacity: 0.5, cursor: 'not-allowed', filter: 'grayscale(1)' } : undefined}
              title={hasTagConflicts ? 'Hay tags duplicados que deben corregirse antes de guardar' : undefined}
            >
              Guardar Cambios
            </button>
          </div>
        )}

        <div className="rp-footer">
          <div className="rp-updated">Última actualización: {new Date().toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          <button type="button" className="rp-close-link" onClick={handleAttemptClose}>Cerrar</button>
        </div>
      </div>

      <ConfirmDialog
        open={showUnsavedConfirm}
        title="Cambios sin guardar"
        message="Hay cambios sin guardar. ¿Deseas cerrar sin guardar?"
        cancelLabel="Cancelar"
        confirmLabel="Salir sin guardar"
        tone="danger"
        onCancel={() => setShowUnsavedConfirm(false)}
        onConfirm={() => {
          setShowUnsavedConfirm(false);
          setDirty(false);
          onClose();
        }}
      />

      <ConfirmDialog
        open={showSuccessConfirm}
        title="Perfil actualizado"
        message="Los datos del residente se guardaron correctamente."
        confirmLabel="Entendido"
        showCancel={false}
        tone="success"
        onConfirm={() => setShowSuccessConfirm(false)}
      />
    </div>
  );
}
